import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableItem,
	ContentTableColumn
} from "src/components/ContentTable/ContentTable";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useAnnouncements from "../../hooks/useAnnouncements";
import useDeleteAnnouncement from "../../hooks/useDeleteAnnouncement";
import { mapAnnouncementsToContentTableItems } from "../../utils/mapAnnouncementsToContentTableItems";
import {
	getAudienceSummary,
	getRelevantDate,
	getDateLabel,
	isDraftAnnouncement
} from "../../utils/announcementListHelpers";

type AnnouncementStatus = "draft" | "scheduled" | "sent" | "expired";

const statusConfig: Record<
	AnnouncementStatus,
	{ label: string; className: string }
> = {
	draft: { label: "Draft", className: "bg-muted text-muted-foreground" },
	scheduled: { label: "Scheduled", className: "bg-primary/10 text-primary" },
	sent: { label: "Sent", className: "bg-emerald-50 text-emerald-700" },
	expired: { label: "Expired", className: "bg-red-50 text-red-700" }
};

const announcementColumns: ContentTableColumn[] = [
	{
		key: "status",
		label: "Status",
		width: "120px",
		render: (item) => {
			const status = (item.data?.status as AnnouncementStatus) ?? "draft";
			const config = statusConfig[status];
			return (
				<span
					className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.className}`}
				>
					{config.label}
				</span>
			);
		}
	},
	{
		key: "audience",
		label: "Audience",
		width: "140px",
		render: (item) => getAudienceSummary(item)
	},
	{
		key: "date",
		label: "Date",
		render: (item) => (
			<span>
				<span className="text-muted-foreground text-xs">
					{getDateLabel(item)}
				</span>
				<br />
				{getRelevantDate(item)}
			</span>
		)
	}
];

const Announcements = () => {
	const navigate = useNavigate();
	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);
	const { data: allAnnouncements = [] } = useAnnouncements();
	const { mutate: deleteAnnouncement, isLoading: isDeleting } =
		useDeleteAnnouncement();
	const items = mapAnnouncementsToContentTableItems(allAnnouncements);

	return (
		<Page>
			<ContentTable
				items={items}
				columns={announcementColumns}
				showCreatedBy={true}
				showLastUpdated={false}
				showOrder={false}
				showStatus={false}
				onRowClick={(item) => navigate(`/announcements/${item.id}`)}
				onDelete={(item) => setItemToDelete(item)}
				canDelete={isDraftAnnouncement}
				createPath="/announcements/create"
				createLabel="New Announcement"
				searchPlaceholder="Search announcements..."
			/>
			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete announcement?"
				description="This action cannot be undone."
				onCancel={() => {
					if (!isDeleting) setItemToDelete(null);
				}}
				cancelBtnProps={{ disabled: isDeleting }}
				onSubmit={() => {
					if (itemToDelete) {
						deleteAnnouncement(Number(itemToDelete.id), {
							onSuccess: () => setItemToDelete(null)
						});
					}
				}}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default Announcements;
